import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { CountReviewProduct, InventoryProductSource } from '../../../services/inventory/inventoryService';
import { fmtQuantity } from './inventoryReviewShared';

/**
 * What the modal is setting a price for. Built either from a "Missing price" count-review
 * row (`setPriceTargetFromReviewRow`) or straight from a configured section product on the
 * inventory config page — the durable price is restaurant-wide either way.
 */
export interface SetPriceTarget {
  productKey: string;
  productName: string;
  source: InventoryProductSource;
  /** The unit the entered price is expressed in. */
  countUnit: string;
  /** Section the product sits in — context line only. */
  sectionName: string;
  inventorySectionProductId: string | null;
  /** Set only when opened from a saved count; that frozen count is then patched too. */
  countId?: string;
  count?: number;
}

export function setPriceTargetFromReviewRow(row: CountReviewProduct): SetPriceTarget {
  return {
    productKey: row.productKey,
    productName: row.productName,
    source: row.source,
    countUnit: row.countUnit,
    sectionName: row.sectionName,
    inventorySectionProductId: row.inventorySectionProductId,
    // `configMissingPrice` rows are never-counted config products: no count to patch,
    // so the durable price is the only write.
    countId: row.countId || undefined,
    count: row.neverCounted ? undefined : row.count,
  };
}

interface Props {
  /** The product being priced. Null → modal renders nothing. */
  product: SetPriceTarget | null;
  restaurantId: string;
  onClose: () => void;
}

/**
 * Set-price modal for products inventory can't value. The price is entered ex VAT per the
 * product's count unit and is stored DURABLY, restaurant-wide: it becomes the valuation
 * fallback for every future count wherever the live order price is null or 0 (a real
 * invoice price always wins again). Order history is never touched — a €0 invoice line
 * stays €0 for spend/cost%/price alerts. Custom ingredients take it as their global price
 * (VAT rate required for the incl-VAT value). When opened from a saved count, that already
 * frozen count is patched as well so the number the user is looking at updates immediately.
 */
export default function SetInventoryPriceModal({ product, restaurantId, onClose }: Props) {
  const { t } = useTranslation();
  const setCountProductPrice = useInventoryStore((s) => s.setCountProductPrice);
  const setProductPriceOverride = useInventoryStore((s) => s.setProductPriceOverride);

  const [price, setPrice] = useState('');
  const [vatRate, setVatRate] = useState<'9' | '21'>('9');
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isOpen = product != null;

  useEffect(() => {
    if (!isOpen) return;
    setPrice('');
    setVatRate('9');
    setSaving(false);
    setHasError(false);
  }, [isOpen]);

  // Lock background page scroll while the drawer is open (same idiom as the
  // cost-price detail modals). Cleanup restores it on close or unmount.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const isCustom = product?.source === 'custom';
  // A subrecipe's price is derived from its own ingredients, so the durable, restaurant-wide
  // override is blocked for preps (the server rejects it too) — only the frozen count can be
  // corrected. Without a count behind the row there is nothing this modal can write.
  const isSubrecipe = product?.source === 'subrecipe';
  const subrecipeWithoutCount = isSubrecipe && !product?.countId;
  const priceNum = parseFloat(price);
  const validPrice = !isNaN(priceNum) && priceNum > 0 && !subrecipeWithoutCount;
  const rateDecimal = vatRate === '21' ? 0.21 : 0.09;
  const inclVatPreview = isCustom && validPrice ? Math.round(priceNum * (1 + rateDecimal) * 100) / 100 : null;

  const handlePriceChange = (value: string) => {
    // Only allow numbers and decimal points (same as the cost-price ingredient modals).
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) setPrice(value);
  };

  const handleSave = async () => {
    if (!validPrice || saving || !product) return;
    setSaving(true);
    setHasError(false);
    try {
      // Patch the frozen count first so the row the user is looking at updates.
      if (product.countId) {
        await setCountProductPrice(
          product.countId,
          {
            productKey: product.productKey,
            source: product.source,
            priceExVat: priceNum,
            ...(isCustom ? { taxRate: rateDecimal } : {}),
            inventorySectionProductId: product.inventorySectionProductId,
          },
          restaurantId
        );
      }
      // Then make it durable. The count endpoint is ALREADY durable for custom
      // ingredients (it writes the ingredient's own price), so skip the second write there.
      // Subrecipes never get a durable override — the prep keeps the price its ingredients give it.
      if (!isSubrecipe && (!product.countId || !isCustom)) {
        await setProductPriceOverride(restaurantId, {
          productKey: product.productKey,
          priceExVat: priceNum,
          countUnit: product.countUnit,
          ...(isCustom ? { taxRate: rateDecimal } : {}),
          inventorySectionProductId: product.inventorySectionProductId,
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to set inventory product price:', err);
      setHasError(true);
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-[584px]',
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar with back arrow */}
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 pt-3 sm:pt-5 pb-3 sm:pb-4 flex flex-col">
          {product && (
            <>
              {/* Title + description */}
              <div className="flex flex-col gap-2 sm:gap-3 mb-5 sm:mb-6">
                <h2 className="text-[24px] sm:text-[28px] leading-[36px] sm:leading-[39px] font-semibold sm:font-bold text-[#161924] dark:text-zinc-50">
                  {t('inventory.setPriceTitle')}
                </h2>
                <p className="text-[14px] sm:text-[16px] leading-[18px] sm:leading-[21px] font-normal text-[#475467] dark:text-[#CBD5E1]">
                  {isSubrecipe
                    ? t('inventory.setPriceDescriptionSubrecipe')
                    : isCustom
                      ? t('inventory.setPriceDescriptionCustom')
                      : t('inventory.setPriceDescription')}
                </p>
              </div>

              {/* Product name */}
              <p className="mb-4 text-[16px] leading-[30px] font-semibold text-[#101828] dark:text-zinc-50">
                {product.productName}
              </p>

              <div className="mb-5 sm:mb-6 grid grid-cols-2 gap-2.5 sm:gap-4">
                <div className="rounded-xl border border-[#E5E7EB] dark:border-[#0E2B42] dark:bg-[#0E2B42] px-4 sm:px-5 py-3 sm:py-[13px] flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                  <span className="text-[18px] leading-[24px] font-semibold text-[#101828] dark:text-[#F8FAFC]">
                    {t('inventory.section')}
                  </span>
                  <span className="text-[24px] leading-[38px] font-semibold text-[#0C111D] dark:text-zinc-50">
                    {product.sectionName}
                  </span>
                </div>

                {/* Counted card only when opened from a saved count; the config page has nothing counted yet. */}
                {product.count != null && (
                  <div className="rounded-xl border border-[#E5E7EB] dark:border-[#0E2B42] dark:bg-[#0E2B42] px-4 sm:px-5 py-3 sm:py-[13px] flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                    <span className="text-[18px] leading-[24px] font-semibold text-[#101828] dark:text-[#F8FAFC]">
                      {t('inventory.counted')}
                    </span>
                    <span className="text-[24px] leading-[38px] font-semibold text-[#0C111D] dark:text-zinc-50">
                      {fmtQuantity(product.count)} {product.countUnit}
                    </span>
                  </div>
                )}
              </div>

              {hasError && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">{t('inventory.setPriceError')}</p>
                </div>
              )}

              {subrecipeWithoutCount && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">{t('inventory.setPriceSubrecipeBlocked')}</p>
                </div>
              )}

              {/* VAT rate (custom ingredients only — needed for the global incl-VAT price) */}
              {isCustom && (
                <div>
                  <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                    {t('costPrice.vatRate.label')} *
                  </label>
                  <div className="flex gap-2">
                    {(['9', '21'] as const).map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        onClick={() => setVatRate(rate)}
                        className={cn(
                          'flex-1 px-4 py-2 rounded-xl border',
                          semiBoldText,
                          vatRate === rate
                            ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                            : cn(borderPrimary, secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')
                        )}
                      >
                        {rate}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price ex VAT per counted unit */}
              <div className="mt-5 sm:mt-6 rounded-lg border border-[#E8E8E9] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] p-4 flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                <span className="text-[16px] leading-[28px] font-medium text-[#344054] dark:text-[#CBD5E1]">
                  {t('inventory.setPricePerUnit', { unit: product.countUnit })} *
                </span>
                <div className="flex items-center gap-1.5 rounded-lg px-2.5 sm:px-4 h-11 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040] bg-white dark:bg-[#081C2C] border border-[#D0D5DD] dark:border-[#1E3A4E]">
                  <span className="text-[16px] leading-5 font-semibold text-[#6B7280] dark:text-zinc-400">€</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                    className="flex-1 bg-transparent dark:bg-transparent text-[16px] leading-5 font-semibold text-[#111827] dark:text-zinc-50 placeholder:text-[#9CA3AF] outline-none"
                  />
                </div>
                {inclVatPreview != null && (
                  <p className={cn('text-xs', secondaryText)}>
                    {t('inventory.setPriceInclVatPreview', { price: `€${inclVatPreview.toFixed(2)}`, rate: vatRate })}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 sm:gap-6 sm:px-5 p-4 sm:py-5 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
          >
            {t('inventory.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !validPrice}
            className="flex-1 px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? t('inventory.loading') : t('inventory.setPriceConfirm')}
          </button>
        </div>
      </div>
    </>
  );
}
